import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth, useCurrency } from '@/App';
import { Menu, LayoutDashboard, Package, ShoppingCart, CalendarArrowDown, FileText, Users, Settings, LogOut, DollarSign, ReceiptText, SquareUserRound, ClipboardList } from 'lucide-react';

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { currency, toggleCurrency } = useCurrency();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Panel Principal', path: '/', roles: ['Admin', 'Manager', 'Salesperson'] },
    { icon: Package, label: 'Productos', path: '/products', roles: ['Admin', 'Manager', 'Salesperson'] },
    { icon: CalendarArrowDown, label: 'Órdenes de Compra', path: '/purchase-orders', roles: ['Admin', 'Manager'] },
    { icon: ShoppingCart, label: 'Ventas', path: '/sales', roles: ['Admin', 'Manager', 'Salesperson'] },
    { icon: DollarSign, label: 'Cuentas a Cobrar', path: '/credit-sales', roles: ['Admin', 'Manager'] },
    { icon: ReceiptText, label: 'Facturas', path: '/invoices', roles: ['Admin', 'Manager', 'Salesperson'] },
    { icon: FileText, label: 'Reportes', path: '/reports', roles: ['Admin', 'Manager'] },
    { icon: ClipboardList, label: 'Devoluciones(Inspección)', path: '/returns-inspection', roles: ['Admin', 'Manager'] },
    { icon: DollarSign, label: 'Gastos Fijos', path: '/fixed-expenses', roles: ['Admin', 'Manager'] },
    { icon: SquareUserRound, label: 'Clientes', path: '/clients', roles: ['Admin', 'Manager', 'Salesperson'] },
    { icon: Users, label: 'Usuarios', path: '/users', roles: ['Admin', 'Manager'] },
    { icon: Settings, label: 'Configuración', path: '/settings', roles: ['Admin', 'Manager'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--card))]/80 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--card))]/60">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Abrir menú" data-testid="sidebar-open-button">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <div className="flex flex-col h-full">
                <div className="mt-6 mb-4">
                  <h2 className="text-xl font-semibold" data-font="display">Aurelia Scent OS</h2>
                  <p className="text-sm text-muted-foreground mt-1">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
                <nav className="flex flex-col gap-2 flex-1" aria-label="Principal">
                  {filteredMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Button
                        key={item.path}
                        variant={isActive ? "secondary" : "ghost"}
                        className="justify-start"
                        onClick={() => handleNavigation(item.path)}
                        data-testid={`nav-${item.label.toLowerCase()}-link`}
                      >
                        <Icon className="mr-2 size-4" />
                        {item.label}
                      </Button>
                    );
                  })}
                </nav>
                <Button variant="ghost" className="justify-start mt-auto" onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 size-4" />
                  Cerrar Sesión
                </Button>
              </div>
              <SheetTitle></SheetTitle>
              <SheetDescription></SheetDescription>
            </SheetContent>
          </Sheet>

          <div className="flex-1">
            <h1 className="text-lg font-semibold" data-font="display">Aurelia Scent OS</h1>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleCurrency}
              data-testid="currency-toggle"
              className="transition-colors"
            >
              <DollarSign className="mr-1 size-4" />
              {currency}
            </Button>
            <Button
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[#BFA774] transition-colors"
              size="sm"
              onClick={() => navigate('/sales')}
              data-testid="dashboard-quick-new-sale-button"
            >
              Nueva Venta
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>
    </div>
  );
}
